import { RefObject, useLayoutEffect, useState } from 'react';

interface BackdropLayout {
    top: number;
    left: number;
    right: number;
    height: number;
}

type BackdropStyle = BackdropLayout &
    Pick<
        React.CSSProperties,
        | 'paddingTop'
        | 'paddingBottom'
        | 'paddingLeft'
        | 'paddingRight'
        | 'fontFamily'
        | 'fontSize'
        | 'fontWeight'
        | 'fontStyle'
        | 'fontStretch'
        | 'fontKerning'
        | 'lineHeight'
        | 'letterSpacing'
        | 'wordSpacing'
        | 'textRendering'
        | 'textIndent'
        | 'textSizeAdjust'
        | 'WebkitTextSizeAdjust'
    >;

/**
 * Measures the textarea's position and computed styles so the backdrop overlay
 * can be placed directly on top. Re-measures on every resize via ResizeObserver.
 *
 * Android WebView renders textarea text with slightly different sub-pixel metrics
 * than a plain div, so we apply small corrections to keep the two layers aligned.
 */
export const useBackdropStyle = (inputRef: RefObject<HTMLInputElement | null>): React.CSSProperties => {
    const [backdropStyle, setBackdropStyle] = useState<React.CSSProperties>({});

    useLayoutEffect(() => {
        const el = inputRef.current;
        if (!el) return undefined;

        const measure = () => {
            const parent = el.offsetParent as HTMLElement | null;
            const parentWidth = parent?.offsetWidth ?? el.offsetWidth;
            const styles = getComputedStyle(el);

            const baseStyle: BackdropStyle = {
                top: el.offsetTop,
                left: el.offsetLeft,
                right: parentWidth - el.offsetLeft - el.offsetWidth,
                height: el.offsetHeight,
                paddingTop: styles.paddingTop,
                paddingBottom: styles.paddingBottom,
                paddingLeft: styles.paddingLeft,
                paddingRight: styles.paddingRight,
                fontFamily: styles.fontFamily,
                fontSize: styles.fontSize,
                fontWeight: Number(styles.fontWeight),
                fontStyle: styles.fontStyle,
                fontStretch: styles.fontStretch as React.CSSProperties['fontStretch'],
                fontKerning: styles.fontKerning as React.CSSProperties['fontKerning'],
                lineHeight: styles.lineHeight,
                letterSpacing: styles.letterSpacing,
                wordSpacing: styles.wordSpacing,
                textRendering: styles.textRendering as React.CSSProperties['textRendering'],
                textIndent: styles.textIndent,
                // Force 100% to prevent Android WebView from independently auto-scaling
                // the backdrop div vs the textarea. The OS font scale is already
                // reflected in the computed fontSize we copy above; this stops the
                // browser from applying additional font boosting on top of it.
                textSizeAdjust: '100%',
                WebkitTextSizeAdjust: '100%'
            };
            setBackdropStyle(baseStyle);
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [inputRef]);

    return backdropStyle;
};
